import Toast from 'react-native-toast-message';

import type {PaymentMethodType} from '../../../components/payments/till/PaymentMethodSelector/types';
import {ERROR_MESSAGE} from '../../../components/ToastBottom';
import type {PaymentCard} from '../../../queries/paymentCard';
import type {CartStore} from '../../../stores/cartStore';
import type {UserStore} from '../../../stores/userStore';
import {logger} from '../../../utils/logger';

// ============================================================================
// TYPES
// ============================================================================

export interface OrderValidationParams {
  cartStore: CartStore;
  userStore: UserStore;
}

export interface PaymentValidationParams extends OrderValidationParams {
  /** Payment card from usePaymentCard() hook - required for payment method validation */
  paymentCard?: PaymentCard | null;
  selectedPaymentMethod?: PaymentMethodType;
}

export type UsePaymentValidationParams = PaymentValidationParams;

export interface PaymentValidationResult {
  /** Get current validation error, null if valid. Computed fresh each call. */
  getValidationError: () => string | null;
  /** Validate and show toast on error, returns true if valid */
  validateWithToast: () => boolean;
  /** Whether order can be placed (computed fresh) */
  canPlaceOrder: () => boolean;
}

// ============================================================================
// CANONICAL VALIDATION FUNCTIONS
// ============================================================================

/**
 * Common order validation - used by intermediate checkout steps.
 * Checks: empty order, billing address, slot, nominated person.
 *
 * This is the CANONICAL implementation. All other validation should use this.
 */
export function computeOrderValidationError(
  cartStore: CartStore,
  userStore: UserStore,
): string | null {
  if (!cartStore.currentOrder?.order_lines?.length) {
    return 'Cannot place an empty order';
  }

  if (!userStore.billingAddress) {
    return 'Please set a Billing Address';
  }

  if (!cartStore.selectedSlot) {
    return 'To place your order please select a delivery or pick up time.';
  }

  if (cartStore.hasNominatedPerson && !cartStore.nominatedPersonName.length) {
    return 'A person (18+) must be nominated to accept the delivery';
  }

  return null;
}

/**
 * Full payment validation - extends common validation with payment method checks.
 * Used by final payment screens before order submission.
 *
 * This is the CANONICAL implementation. All payment validation should use this.
 */
export function computePaymentValidationError(
  cartStore: CartStore,
  userStore: UserStore,
  paymentCard: PaymentCard | null | undefined,
  selectedPaymentMethod: PaymentMethodType,
): string | null {
  // Run common validation first
  const commonError = computeOrderValidationError(cartStore, userStore);
  if (commonError) {
    return commonError;
  }

  // Apple Pay handles its own validation via payment sheet
  if (selectedPaymentMethod === 'applePay') {
    logger.info('PaymentValidation', 'Bypassing card validation for Apple Pay');
    return null;
  }

  // Payment method validation
  const {isGiftCardsBeingUsed} = cartStore;
  const isGiftCardsCoveringTotal = cartStore.totalChargedOnGiftCardsCents >= cartStore.totalAmount;

  // STRICT check: card must exist AND have valid id for checkout
  const hasValidPaymentMethod = !!paymentCard?.id;

  // Gift cards cover full amount - no card needed
  if (isGiftCardsBeingUsed && isGiftCardsCoveringTotal) {
    return null;
  }

  // Gift cards don't cover total - need card
  if (isGiftCardsBeingUsed && !isGiftCardsCoveringTotal && !hasValidPaymentMethod) {
    return "The gift cards do not cover the total amount and there's no other payment method available. Please add a debit/credit card";
  }

  // No gift cards - need card
  if (!isGiftCardsBeingUsed && !hasValidPaymentMethod) {
    logger.warn('PaymentValidation', 'No valid payment method', {
      hasPaymentCard: !!paymentCard,
      hasPaymentCardId: !!paymentCard?.id,
      paymentCardId: paymentCard?.id,
    });
    return 'Cannot place an order without a valid payment method';
  }

  return null;
}

// ============================================================================
// REACT HOOK
// ============================================================================

/**
 * Hook for payment validation logic.
 * Returns plain functions (not useCallback) to avoid stale closure issues with MobX.
 *
 * Why no useCallback:
 * - MobX observer() only tracks reads during render, not inside callbacks
 * - useCallback with store refs creates stale closures when store values change
 * - Plain functions read current values at call time (correct behavior)
 */
export function usePaymentValidation({
  cartStore,
  userStore,
  paymentCard,
  selectedPaymentMethod = 'card',
}: UsePaymentValidationParams): PaymentValidationResult {
  // Plain functions - read current values at call time
  const getValidationError = (): string | null => {
    return computePaymentValidationError(cartStore, userStore, paymentCard, selectedPaymentMethod);
  };

  const validateWithToast = (): boolean => {
    const error = getValidationError();
    if (error) {
      logger.warn('PaymentValidation', 'Order validation failed', {error});
      Toast.show({type: ERROR_MESSAGE, text1: error});
      return false;
    }
    return true;
  };

  const canPlaceOrder = (): boolean => {
    return getValidationError() === null;
  };

  return {
    getValidationError,
    validateWithToast,
    canPlaceOrder,
  };
}
